// src/components/Toast.js — ระบบ Toast notification แบบทันสมัย
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';

const ToastContext = createContext(null);

const ICONS = {
  success: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" fill="#43a047" stroke="#43a047"/>
      <polyline points="8 12 11 15 16 9" stroke="#fff"/>
    </svg>
  ),
  error: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" fill="#e53935" stroke="#e53935"/>
      <line x1="15" y1="9" x2="9" y2="15" stroke="#fff"/><line x1="9" y1="9" x2="15" y2="15" stroke="#fff"/>
    </svg>
  ),
  warning: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" fill="#ef6c00" stroke="#ef6c00"/>
      <line x1="12" y1="8" x2="12" y2="13" stroke="#fff"/><circle cx="12" cy="16" r="0.5" fill="#fff" stroke="#fff"/>
    </svg>
  ),
  info: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" fill="#1976d2" stroke="#1976d2"/>
      <line x1="12" y1="16" x2="12" y2="12" stroke="#fff"/><circle cx="12" cy="8" r="0.5" fill="#fff" stroke="#fff"/>
    </svg>
  ),
};

const STYLES = {
  success: { bg: '#e8f5e9', border: '#a5d6a7', color: '#1b5e20' },
  error: { bg: '#ffebee', border: '#ef9a9a', color: '#b71c1c' },
  warning: { bg: '#fff3e0', border: '#ffcc80', color: '#e65100' },
  info: { bg: '#e3f2fd', border: '#90caf9', color: '#0d47a1' },
};

let toastId = 0;

function ToastItem({ toast, onRemove }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onRemove(toast.id), 300);
    }, toast.duration || 3000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  const s = STYLES[toast.type] || STYLES.info;

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 16px', borderRadius: 12,
        background: s.bg, border: `1px solid ${s.border}`,
        color: s.color, fontSize: 13, fontWeight: 600,
        boxShadow: '0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
        transform: visible && !exiting ? 'translateX(0)' : 'translateX(120%)',
        opacity: visible && !exiting ? 1 : 0,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer', maxWidth: 380, minWidth: 200,
        pointerEvents: 'auto',
      }}
      onClick={() => { setExiting(true); setTimeout(() => onRemove(toast.id), 300); }}
    >
      {ICONS[toast.type] || ICONS.info}
      <span style={{ flex: 1, lineHeight: 1.4 }}>{toast.message}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, flexShrink: 0 }}>
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback((message, type = 'info', duration = 3000) => {
    addToast(message, type, duration);
  }, [addToast]);

  toast.success = (msg, dur) => addToast(msg, 'success', dur);
  toast.error = (msg, dur) => addToast(msg, 'error', dur || 4000);
  toast.warning = (msg, dur) => addToast(msg, 'warning', dur);
  toast.info = (msg, dur) => addToast(msg, 'info', dur);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Toast container — fixed top-right */}
      <div style={{
        position: 'fixed', top: 16, right: 16, zIndex: 99999,
        display: 'flex', flexDirection: 'column', gap: 8,
        pointerEvents: 'none',
      }}>
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be inside ToastProvider');
  return ctx;
}
